<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { formatTime } from '@chatwoot/utils';
import ReportLineChart from '../ReportLineChart.vue';
import ReportCard from '../ReportCard.vue';

const props = defineProps({
  entityName: {
    type: String,
    default: '',
  },
  reportKeys: {
    type: Object,
    default: () => ({}),
  },
  selectedMetric: {
    type: String,
    default: '',
  },
});

defineEmits(['metricSelect']);

const accountSummary = useMapGetter('getAccountSummary');
const accountReport = useMapGetter('getAccountReports');
const summaryUIFlags = useMapGetter('getReportSummaryUIFlags');

const chartReportData = computed(() => accountReport.value.data || {});

const volumeDefinitions = [
  ['CONVERSATIONS', 'Conversas', 'chat', '#1f93ff'],
  ['INCOMING_MESSAGES', 'Mensagens recebidas', 'arrow-download', '#14b8a6'],
  ['OUTGOING_MESSAGES', 'Mensagens enviadas', 'send', '#8b5cf6'],
  ['RESOLUTION_COUNT', 'Resoluções', 'checkmark-circle', '#44CE4B'],
];

const efficiencyDefinitions = [
  ['FIRST_RESPONSE_TIME', 'Primeira resposta', 'calendar-clock'],
  ['REPLY_TIME', 'Espera do cliente', 'status'],
  ['RESOLUTION_TIME', 'Tempo de resolução', 'document-list-clock'],
];

const volumeMetrics = computed(() =>
  volumeDefinitions
    .filter(([name]) => props.reportKeys[name])
    .map(([name, label, icon, color]) => {
      const key = props.reportKeys[name];
      return { key, label, icon, color, value: accountSummary.value[key] || 0 };
    })
);

const efficiencyMetrics = computed(() =>
  efficiencyDefinitions
    .filter(([name]) => props.reportKeys[name])
    .map(([name, label, icon]) => {
      const key = props.reportKeys[name];
      const current = Number(accountSummary.value[key] || 0);
      const previous = Number(accountSummary.value.previous?.[key] || 0);
      return {
        key,
        label,
        icon,
        current,
        previous,
        trend: previous
          ? Math.round(((current - previous) / previous) * 100)
          : 0,
      };
    })
);

const resolvedCount = computed(() =>
  Number(accountSummary.value.resolved_conversations_count || 0)
);
const conversationCount = computed(() =>
  Number(accountSummary.value.conversations_count || 0)
);
const unresolvedCount = computed(() =>
  Math.max(conversationCount.value - resolvedCount.value, 0)
);
const resolutionRate = computed(() =>
  conversationCount.value
    ? Math.round((resolvedCount.value / conversationCount.value) * 100)
    : 0
);

const primarySeries = computed(() => {
  const metric = volumeMetrics.value.find(
    item => chartReportData.value[item.key]?.length
  );
  return metric ? chartReportData.value[metric.key] : [];
});

const labels = computed(() =>
  primarySeries.value.map(item =>
    format(fromUnixTime(item.timestamp), 'dd MMM')
  )
);

const chartCollection = computed(() => ({
  labels: labels.value,
  datasets: volumeMetrics.value.map(metric => ({
    label: metric.label,
    data: (chartReportData.value[metric.key] || []).map(
      item => item.value ?? item.count ?? 0
    ),
    borderColor: metric.color,
    backgroundColor: metric.color,
  })),
}));

const isLoading = key => Boolean(accountReport.value.isFetching?.[key]);
const isSummaryLoading = computed(
  () => summaryUIFlags.value.isFetchingAccountSummary
);
const isChartLoading = computed(() =>
  volumeMetrics.value.some(metric => isLoading(metric.key))
);
</script>

<template>
  <div class="space-y-4">
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <ReportCard
        v-for="metric in volumeMetrics"
        :key="metric.key"
        :is-loading="isSummaryLoading"
        selectable
        :selected="metric.key === selectedMetric"
        @select="$emit('metricSelect', metric)"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="mb-2 text-sm text-slate-600 dark:text-slate-300">
              {{ metric.label }}
            </p>
            <strong class="text-4xl text-slate-900 dark:text-slate-50">
              {{ Number(metric.value).toLocaleString() }}
            </strong>
          </div>
          <span class="entity-icon">
            <fluent-icon :icon="metric.icon" size="22" />
          </span>
        </div>
      </ReportCard>
    </div>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-12">
      <ReportCard class="xl:col-span-8" :is-loading="isChartLoading">
        <div class="mb-4">
          <h3
            class="mb-0 text-lg font-medium text-slate-900 dark:text-slate-50"
          >
            Evolução de {{ entityName || 'atendimentos' }}
          </h3>
        </div>
        <div v-if="labels.length" class="w-full h-64">
          <ReportLineChart :collection="chartCollection" />
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-sm text-slate-500 dark:text-slate-400"
        >
          Sem dados suficientes para o gráfico.
        </div>
      </ReportCard>

      <ReportCard class="xl:col-span-4" :is-loading="isSummaryLoading">
        <h3 class="mb-5 text-lg font-medium text-slate-900 dark:text-slate-50">
          Distribuição de resoluções
        </h3>
        <div class="flex flex-col items-center gap-6 sm:flex-row xl:flex-col">
          <div
            class="flex items-center justify-center flex-shrink-0 w-40 h-40 rounded-full"
            :style="{
              background: `conic-gradient(#44CE4B 0 ${resolutionRate * 3.6}deg, #334155 ${resolutionRate * 3.6}deg 360deg)`,
            }"
          >
            <span
              class="flex flex-col items-center justify-center flex-shrink-0 w-28 h-28 rounded-full bg-white dark:bg-slate-800"
            >
              <strong class="text-3xl text-slate-900 dark:text-slate-50">
                {{ resolutionRate }}%
              </strong>
              <span class="text-xs text-slate-500 dark:text-slate-400">
                resolvidas
              </span>
            </span>
          </div>
          <div class="w-full space-y-3 text-sm">
            <button
              type="button"
              class="flex items-center justify-between w-full p-2 -m-2 text-left rounded-md transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:hover:bg-slate-700"
              :class="{
                'bg-woot-50 ring-1 ring-woot-500 dark:bg-woot-900/40':
                  selectedMetric === 'resolved_conversations_count',
              }"
              :aria-pressed="selectedMetric === 'resolved_conversations_count'"
              @click="
                $emit('metricSelect', {
                  key: 'resolved_conversations_count',
                  label: 'Resolvidas',
                })
              "
            >
              <span class="text-slate-600 dark:text-slate-300">Resolvidas</span>
              <strong class="text-green-600 dark:text-green-400">{{
                resolvedCount
              }}</strong>
            </button>
            <button
              type="button"
              class="flex items-center justify-between w-full p-2 -m-2 text-left rounded-md transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:hover:bg-slate-700"
              :class="{
                'bg-woot-50 ring-1 ring-woot-500 dark:bg-woot-900/40':
                  selectedMetric === 'unresolved_conversations_count',
              }"
              :aria-pressed="
                selectedMetric === 'unresolved_conversations_count'
              "
              @click="
                $emit('metricSelect', {
                  key: 'unresolved_conversations_count',
                  label: 'Não resolvidas',
                })
              "
            >
              <span class="text-slate-600 dark:text-slate-300">
                Não resolvidas
              </span>
              <strong class="text-slate-700 dark:text-slate-200">{{
                unresolvedCount
              }}</strong>
            </button>
          </div>
        </div>
      </ReportCard>
    </div>

    <ReportCard :is-loading="isSummaryLoading">
      <h3 class="mb-5 text-lg font-medium text-slate-900 dark:text-slate-50">
        Eficiência operacional
      </h3>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div
          v-for="metric in efficiencyMetrics"
          :key="metric.key"
          class="relative flex items-center gap-4 p-4 rounded-md bg-slate-25 dark:bg-slate-900"
        >
          <span class="entity-icon">
            <fluent-icon :icon="metric.icon" size="22" />
          </span>
          <div>
            <p class="mb-1 text-sm text-slate-500 dark:text-slate-400">
              {{ metric.label }}
            </p>
            <strong class="text-2xl text-slate-900 dark:text-slate-50">
              {{ formatTime(metric.current) }}
            </strong>
            <span
              v-if="metric.trend"
              class="block mt-1 text-xs font-medium"
              :class="metric.trend <= 0 ? 'text-green-500' : 'text-red-500'"
            >
              {{ metric.trend > 0 ? '+' : '' }}{{ metric.trend }}% vs. período
              anterior
            </span>
          </div>
        </div>
      </div>
    </ReportCard>
  </div>
</template>

<style lang="scss" scoped>
.entity-icon {
  @apply flex items-center justify-center flex-shrink-0 w-11 h-11 rounded-full text-woot-500 bg-woot-50 dark:bg-woot-900/40;
}
</style>
